import { useCallback, useState } from 'react';
import { DestinationCGType } from '@/types/graphql/root';
import { TRIP_CONDITIONS, TRIP_MODE, TripModeType } from '@/app/ui/trip/client-trip-page';

interface UseTripScrollProps {
  destinations: DestinationCGType[];
  mode: TripModeType;
  setTopDestInMap: (dest: DestinationCGType | null) => void;
}

const useTripScroll = ({ destinations, mode, setTopDestInMap }: UseTripScrollProps) => {
  const [focusedDay, setFocusedDay] = useState<number>(1);
  const [topSlot, setTopSlot] = useState<DestinationCGType | null>(null);

  const onScrollHandler = useCallback(() => {
    if (TRIP_CONDITIONS.IS_SEARCH_MODE(mode)) {
      const $thisEl = document.getElementById('planner-inner-itinerary');
      $thisEl?.style.setProperty('padding-bottom', '0');
      return;
    }

    const $thisEl = document.getElementById('planner-inner-itinerary');
    const height = $thisEl?.offsetHeight;
    if (height) {
      const $destCardNodeList = document.querySelectorAll('.dest-card');

      const dayHeaderHeight = 42;
      const lastDestCardHeight = $destCardNodeList[$destCardNodeList.length - 1]?.clientHeight ?? 0;
      const autoSaveInfoHeight = mode === TRIP_MODE.EDIT ? 52 : 0;

      $thisEl.style.setProperty(
        'padding-bottom',
        `${height - dayHeaderHeight - lastDestCardHeight - autoSaveInfoHeight}px`,
      );
    }

    const $dayHeaderNodeList = document.querySelectorAll('.day-header-button');

    let curFocusedDay = 1;
    let minTop = Number.MAX_VALUE;
    let currentDayHeaderBottom = 0;
    $dayHeaderNodeList.forEach(($dayHeader) => {
      const [_, dayNumber] = $dayHeader.id.split('_');
      const rect = $dayHeader.getBoundingClientRect();
      const $dayHeaderHeight = $dayHeader.clientHeight ?? 0;
      const rectTop = +dayNumber === 1 ? rect.top : rect.top - $dayHeaderHeight / 2;
      if (rectTop <= minTop) {
        curFocusedDay = +dayNumber;
        minTop = rect.top;
        currentDayHeaderBottom = rect.bottom;
      }
    });
    setFocusedDay(curFocusedDay);

    const $destCardNodeList = document.querySelectorAll(`#dayContainer_${curFocusedDay} .dest-card`);
    let minGap = Number.MAX_VALUE;
    let minDestId: string | null = null;
    $destCardNodeList.forEach(($destCard) => {
      const gap = Math.abs(currentDayHeaderBottom - $destCard.getBoundingClientRect().top);

      if (gap < minGap) {
        minGap = gap;
        minDestId = $destCard.id.split('_')[1];
      }
    });

    if (minDestId) {
      const _topSlot = destinations.find((_d) => _d.id === minDestId);
      setTopSlot(_topSlot ?? null);
      setTopDestInMap(_topSlot ?? null);
    }
  }, [destinations, mode, setTopDestInMap]);

  return {
    focusedDay,
    topSlot,
    setTopSlot,
    onScrollHandler,
  };
};

export default useTripScroll;
